import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroText,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys, useLenderInviteModelQuery } from '../../../shared/hooks';
import { UnlockUser, ActiveDataRoomUser, UserRoleType } from '../../../shared/models';
import { FormikUtils, HtmlUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface SendInviteFormProps {
	dataRoomGuid: string | undefined;
	user: UnlockUser | null;
	close: () => void;
}

export const SendInviteForm: React.FC<SendInviteFormProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	const modelQuery = useLenderInviteModelQuery(dataRoomGuid, user?.guid, user?.roleType, {
		enabled: user != null,
		retry: false,
	});

	const sendInviteMutation = useMutation(
		async ({ values }: SendInviteFormMutationVariables) => {
			const { data, error } = await getApiResponseData<ActiveDataRoomUser>(
				api.post(getInviteApiUrl(dataRoomGuid, user?.guid, user?.roleType), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid));
				snackbar.enqueueSnackbar('Invite was sent.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={user != null} onClose={() => close()}>
			{modelQuery.isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}
			{modelQuery.isSuccess && (
				<Formik
					initialValues={{
						to: user?.emailAddress,
						subject: modelQuery.data.subject,
						emailBody: modelQuery.data.emailBody,
					}}
					onSubmit={FormikUtils.MutateOnSubmit(sendInviteMutation.mutate)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
						return (
							<CerebroForm
								flatIndicator
								onSubmit={handleSubmit}
								sx={{ marginTop: 3, paddingRight: 3 }}
								scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
							>
								<Grid
									container
									spacing={3}
									sx={{ marginTop: 3 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item xs={8}>
										<CerebroLabel>To</CerebroLabel>
										<CerebroText>
											{values.to} ({user?.firstName} {user?.lastName})
										</CerebroText>
									</Grid>

									<Grid item xs={8}>
										<CerebroLabel id="subject" required>
											Subject
										</CerebroLabel>
										<Field
											id="subject"
											as={CerebroInput}
											name="subject"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('subject', errors, touched)}
										/>
										{FormikUtils.IsInvalid('subject', errors, touched) && (
											<CerebroText variant="error">{errors.subject}</CerebroText>
										)}
									</Grid>

									<Grid item xs={11}>
										<CerebroLabel id="emailBody" required>
											Email Body
										</CerebroLabel>
										<CerebroRichText
											value={values.emailBody ?? ''}
											onChange={updatedValue => setFieldValue('emailBody', updatedValue)}
										/>
									</Grid>

									<Grid item xs={8}>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Send Invite
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			)}
		</CerebroDrawer>
	);
};

type SendInviteFormMutationVariables = FormikUtils.MutateOnSubmitVariables<SendInviteFormValues>;
interface SendInviteFormValues {
	to: string | undefined;
	subject: string;
	emailBody: string;
}

function getInviteApiUrl(
	dataRoomGuid: string | undefined,
	userGuid: string | undefined,
	roleType: UserRoleType | undefined
): string {
	if (isBankUserRoleType(roleType)) {
		return CerebroApiEndpoints.DataRoomUsers.Invite(dataRoomGuid, userGuid);
	}
	return CerebroApiEndpoints.TeamMembers.Invite(dataRoomGuid, userGuid);
}

function isBankUserRoleType(roleType: UserRoleType | undefined): boolean {
	if (roleType == undefined) return false;
	return roleType === 'BankUser' || roleType === 'DataRoomUser';
}
